const clean = (value) => String(value ?? '').trim();

const PLATFORM_LABELS = {
  xhs: '小红书',
  douyin: '抖音',
  wechat: '微信公众号',
  wechat_channels: '微信视频号',
  weixin_channels: '微信视频号',
  kuaishou: '快手',
  baijiahao: '百家号',
  zhihu: '知乎',
  bilibili: 'B站',
  toutiao: '今日头条',
};

const READINESS_COLOR_MAP = {
  ready_to_publish: 'green',
  legacy_only: 'blue',
  needs_login: 'orange',
  device_offline: 'red',
  needs_agent_bind: 'red',
  unsupported: 'default',
  unknown: 'gold',
};

const AUTH_STATE_COLOR_MAP = {
  ready: 'green',
  valid: 'green',
  fresh: 'green',
  expired: 'orange',
  stale: 'orange',
  pending: 'orange',
  unknown: 'orange',
  invalid: 'red',
  failed: 'red',
  error: 'red',
};

const READINESS_PRIORITY_MAP = {
  ready_to_publish: 0,
  legacy_only: 1,
  needs_login: 2,
  unknown: 3,
  device_offline: 4,
  needs_agent_bind: 5,
  unsupported: 6,
};

export const getAccountPlatformLabel = (account) => {
  const platformCode = clean(account?.platform_code || account?.platform).toLowerCase();
  return PLATFORM_LABELS[platformCode] || clean(account?.platform) || clean(account?.platform_code) || '-';
};

export const getAuthStateColor = (authState) => {
  const key = clean(authState).toLowerCase();
  return AUTH_STATE_COLOR_MAP[key] || 'default';
};

export const getAccountReadinessDisplay = (account) => {
  const readiness = account?.login_readiness || {};
  const status = clean(readiness?.status).toLowerCase();
  const statusText = clean(readiness?.status_text) || '状态未知';
  const message = clean(readiness?.message) || '当前账号状态尚未完成判定。';
  const nextAction = clean(readiness?.next_action?.label);
  const authState = clean(readiness?.auth_state);
  const sessionMode = clean(readiness?.session_mode || readiness?.execution_mode);
  const agent = account?.agent_summary || account?.agent || {};

  let agentText = '';
  if (readiness?.agent_bound === false) {
    agentText = '未绑定 Agent';
  } else if (readiness?.agent_online === false && clean(agent?.agent_id)) {
    agentText = '设备离线';
  } else if (readiness?.agent_online === true && clean(agent?.hostname)) {
    agentText = `设备在线 · ${clean(agent.hostname)}`;
  } else if (readiness?.agent_online === true) {
    agentText = '设备在线';
  }

  const parts = [agentText, authState ? `登录态 ${authState}` : '', sessionMode ? `模式 ${sessionMode}` : '']
    .map(clean)
    .filter(Boolean);

  return {
    status,
    statusText,
    statusColor: READINESS_COLOR_MAP[status] || 'default',
    message,
    nextAction,
    authState,
    sessionMode,
    canPublish: Boolean(readiness?.can_publish),
    hintText: parts.join(' · '),
  };
};

export const getAccountReadinessSortPriority = (account) => {
  const display = getAccountReadinessDisplay(account);
  return READINESS_PRIORITY_MAP[display.status] ?? 99;
};

export const sortAccountsByReadiness = (items = []) => (
  [...(items || [])].sort((a, b) => {
    const priorityDiff = getAccountReadinessSortPriority(a) - getAccountReadinessSortPriority(b);
    if (priorityDiff !== 0) return priorityDiff;

    const statusA = clean(a?.status).toLowerCase();
    const statusB = clean(b?.status).toLowerCase();
    if (statusA !== statusB) {
      if (statusA === 'active') return -1;
      if (statusB === 'active') return 1;
    }

    const platformDiff = getAccountPlatformLabel(a).localeCompare(getAccountPlatformLabel(b), 'zh-CN');
    if (platformDiff !== 0) return platformDiff;
    return clean(a?.account_name).localeCompare(clean(b?.account_name), 'zh-CN');
  })
);

export const getAccountSelectionState = (account) => {
  const display = getAccountReadinessDisplay(account);
  const accountStatus = clean(account?.status).toLowerCase();

  if (accountStatus && accountStatus !== 'active') {
    return {
      selectable: false,
      reason: '当前账号状态不是 active，暂不允许选择。',
      display,
    };
  }

  if (display.status === 'unsupported') {
    return {
      selectable: false,
      reason: display.message || '当前账号平台未识别，暂不允许选择。',
      display,
    };
  }

  if (display.status === 'needs_agent_bind') {
    return {
      selectable: false,
      reason: display.message || '当前账号尚未绑定 Agent，暂不允许选择。',
      display,
    };
  }

  return {
    selectable: true,
    reason: '',
    display,
  };
};

export const getAccountRecommendedAction = (account) => {
  const readiness = account?.login_readiness || {};
  const type = clean(readiness?.next_action?.type).toLowerCase();
  const display = getAccountReadinessDisplay(account);

  if (type === 'bind_agent' || type === 'start_agent') {
    return {
      label: '前往账号管理',
      path: '/users',
      reason: display.message,
    };
  }

  if (type === 'agent_login' || type === 'legacy_login') {
    return {
      label: '去账号登录',
      path: '/users',
      reason: display.message,
    };
  }

  return null;
};
